import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'

import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { DateCell, MutedCell, NameCell, StatusChip } from '@/components/TableCells'
import type { DataExport } from '@/services/networks/dataExportsApi'

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  source: 'source',
  type: 'type',
  schedule: 'schedule',
  objectState: 'objectState',
  format: 'format',
  lastSuccessfulExport: 'lastSuccessfulExport',
  updateTime: 'updateTime',
  createTime: 'createTime',
}

interface DataExportTableProps {
  dataExports: DataExport[]
  loading?: boolean
  emptyMessage?: string
  selection?: DataExport[]
  onSelectionChange?: (dataExports: DataExport[]) => void
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
}

export function DataExportTable({
  dataExports,
  loading,
  emptyMessage,
  selection,
  onSelectionChange,
  sort,
  onSortChange,
}: DataExportTableProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const selectable = !!onSelectionChange

  const columns: ColumnDef<DataExport, unknown>[] = [
    ...(selectable ? [selectionColumn<DataExport>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 200 },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    {
      accessorKey: 'description',
      header: t('common.description'),
      enableSorting: false,
      meta: { minWidth: 220 },
      cell: ({ row }) => <MutedCell value={row.original.description} />,
    },
    {
      accessorKey: 'source',
      header: t('dataExports.source'),
      size: 120,
      cell: ({ row }) => <MutedCell value={t(`dataExports.sources.${row.original.source}`)} />,
    },
    {
      accessorKey: 'type',
      header: t('dataExports.destination'),
      size: 150,
      cell: ({ row }) => <MutedCell value={t(`dataExports.types.${row.original.type}`)} />,
    },
    {
      accessorKey: 'schedule',
      header: t('dataExports.schedule'),
      size: 110,
      cell: ({ row }) => (
        <MutedCell
          value={row.original.schedule ? t(`dataExports.schedules.${row.original.schedule}`) : null}
        />
      ),
    },
    {
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => <StatusChip state={row.original.objectState} />,
    },
    optionalColumn<DataExport>({
      accessorKey: 'format',
      header: t('dataExports.format'),
      size: 100,
      cell: ({ row }) => <MutedCell value={row.original.format} />,
    }),
    optionalColumn<DataExport>({
      accessorKey: 'lastSuccessfulExport',
      header: t('dataExports.lastExport'),
      size: 140,
      cell: ({ row }) => <DateCell value={row.original.lastSuccessfulExport} withTime />,
    }),
    optionalColumn<DataExport>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
    optionalColumn<DataExport>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ]

  return (
    <DataTable
      columns={columns}
      data={dataExports}
      loading={loading}
      emptyMessage={emptyMessage ?? t('dataExports.noDataExportsFound')}
      columnsKey="dataExports"
      onRowClick={(row) => navigate(`/admin/data-exports/${row.id}`)}
      {...(selectable ? { selection, onSelectionChange } : {})}
      serverSort={
        onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
      }
    />
  )
}
